const DB_NAME = '__ua__dpop';
const STORE_NAME = 'keys';
// records are keyed by API ORIGIN. Origins carry a scheme, so they cannot collide with these two.
export const LEGACY_RECORD_KEY = 'keypair';
const PROBE_KEY = 'probe';

function requestToPromise<TResult>(request: IDBRequest<TResult>): Promise<TResult> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('indexeddb request failed'));
    };
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      // no indexedDB on the server; some privacy modes reject the open instead
      if (typeof indexedDB === 'undefined') {
        reject(new Error('indexeddb unavailable'));
        return;
      }
      const openRequest = indexedDB.open(DB_NAME, 1);
      openRequest.onupgradeneeded = () => {
        openRequest.result.createObjectStore(STORE_NAME);
      };
      openRequest.onsuccess = () => {
        const db = openRequest.result;
        // devtools "delete database" and eviction close the connection under us
        db.onclose = () => {
          dbPromise = null;
        };
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      openRequest.onerror = () => {
        reject(openRequest.error ?? new Error('indexeddb open failed'));
      };
    });
    // a failed open must not stick, so the next call retries
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

async function withStore<TResult>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<TResult>,
): Promise<TResult> {
  const db = await openDb();
  const transaction = db.transaction(STORE_NAME, mode);
  const result = await requestToPromise(run(transaction.objectStore(STORE_NAME)));
  if (mode === 'readwrite') {
    // success here only means queued — quota errors show up at commit
    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => {
        resolve();
      };
      transaction.onabort = () => {
        reject(transaction.error ?? new Error('indexeddb transaction aborted'));
      };
      transaction.onerror = () => {
        reject(transaction.error ?? new Error('indexeddb transaction failed'));
      };
    });
  }
  return result;
}

export function idbGetKeyPair(recordKey: string): Promise<unknown> {
  return withStore('readonly', (store) => store.get(recordKey));
}

export async function idbPutKeyPair(recordKey: string, value: unknown): Promise<void> {
  await withStore('readwrite', (store) => store.put(value, recordKey));
}

export async function idbDeleteKeyPair(recordKey: string): Promise<void> {
  await withStore('readwrite', (store) => store.delete(recordKey));
}

// a database can open and still drop writes, so this writes and reads back for real
export async function idbProbe(): Promise<boolean> {
  try {
    await withStore('readwrite', (store) => store.put('1', PROBE_KEY));
    const persisted = (await withStore('readonly', (store) => store.get(PROBE_KEY))) === '1';
    await withStore('readwrite', (store) => store.delete(PROBE_KEY));
    return persisted;
  } catch (error) {
    console.warn('IndexedDB is unavailable:', error);
    return false;
  }
}
